import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download, Loader2, Lock, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { http, API } from "@/lib/api";
import { Header, Footer } from "@/components/fc/parts";

const LEVEL = {
  success: { color: "var(--fc-teal)", Icon: CheckCircle2, label: "Éléments favorables réunis" },
  warn: { color: "var(--fc-warn)", Icon: AlertTriangle, label: "Situation à consolider" },
  danger: { color: "var(--fc-danger)", Icon: ShieldAlert, label: "Conditions non réunies" },
};
const STATUS_LABEL = { rempli: "Remplie", non_renseigne: "Non renseignée", non_rempli: "Non remplie" };

function Row({ k, v }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2.5 sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-sm font-medium" style={{ color: "var(--fc-secondary-text)" }}>{k}</span>
      <span className="text-sm text-foreground sm:text-right">{v}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="fc-print-page rounded-2xl border border-border bg-card p-6 sm:p-8">
      <h2 className="mb-4 font-serif text-xl font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

export default function Synthesis() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http
      .get(`/dossiers/${id}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.response?.status === 402 ? "unpaid" : "notfound"));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-lg px-4 py-20 text-center">
          <Lock size={36} className="mx-auto mb-4" style={{ color: "var(--fc-secondary-text)" }} />
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            {err === "unpaid" ? "Fiche non débloquée" : "Fiche introuvable"}
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
            {err === "unpaid"
              ? "Cette fiche n'est accessible qu'après confirmation du paiement."
              : "Ce dossier n'existe pas ou a été supprimé."}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!d) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="animate-spin" style={{ color: "var(--fc-navy)" }} />
      </div>
    );
  }

  const lv = LEVEL[d.level];
  const b = d.bien;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6 lg:px-8" data-testid="synthesis-page">
        <div className="no-print flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-foreground">Votre fiche de synthèse</h1>
            <p className="fc-mono mt-1 text-sm" style={{ color: "var(--fc-navy)" }} data-testid="dossier-reference">
              Réf. {d.reference} · établie le {d.created_at_fr}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="print-button"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground"
            >
              <Printer size={16} /> Imprimer
            </button>
            <a
              href={`${API}/dossiers/${id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-pdf-button"
              className="fc-navy-btn inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold"
            >
              <Download size={16} /> Télécharger le PDF
            </a>
          </div>
        </div>

        {d.is_demo && (
          <div
            className="rounded-2xl border p-4 text-sm font-medium"
            style={{ backgroundColor: "#FBF3D4", borderColor: "var(--fc-gold)", color: "#7A5E12" }}
            data-testid="demo-banner"
          >
            Dossier de démonstration — code promo. Aucun paiement réel n'a été perçu.
          </div>
        )}

        <div className="fc-print-page rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: "var(--fc-surface2)" }}>
              <lv.Icon size={24} style={{ color: lv.color }} />
            </div>
            <div>
              <div className="fc-mono text-[11px] uppercase tracking-wider" style={{ color: lv.color }}>{lv.label}</div>
              <h2 className="font-serif text-2xl font-semibold text-foreground">{d.regime.title}</h2>
              {d.regime.legal_basis && (
                <div className="fc-mono mt-1 text-xs" style={{ color: "var(--fc-secondary-text)" }}>
                  {d.regime.legal_basis}
                </div>
              )}
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground">{d.regime.description}</p>
        </div>

        <Section title="Demandeur & bien concerné">
          <Row k="Demandeur" v={`${d.applicant.prenom} ${d.applicant.nom}`} />
          <Row k="Contact" v={`${d.applicant.email}${d.applicant.telephone ? " · " + d.applicant.telephone : ""}`} />
          <Row k="Rappel partenaire souhaité" v={d.applicant.wants_callback ? "Oui" : "Non"} />
          <Row k="Territoire" v={b.territoire} />
          <Row k="Type de bien" v={b.type_bien_label} />
          <Row k="Succession / indivision" v={b.indivision_label} />
          <Row k="Début d'occupation" v={b.annee_debut || "—"} />
          <Row k="Ancienneté calculée" v={b.annees_possession != null ? `${b.annees_possession} ans` : "—"} />
        </Section>

        <Section title="Les cinq conditions de la possession">
          <div className="space-y-2">
            {d.conditions.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-4 border-b border-border py-2.5">
                <div>
                  <div className="text-sm text-foreground">{c.label}</div>
                  <div className="fc-mono text-[11px]" style={{ color: "var(--fc-secondary-text)" }}>{c.article}</div>
                </div>
                <span
                  className="shrink-0 rounded-full px-2.5 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: "var(--fc-surface2)",
                    color: c.status === "rempli" ? "var(--fc-teal)" : c.status === "non_rempli" ? "var(--fc-danger)" : "var(--fc-secondary-text)",
                  }}
                >
                  {STATUS_LABEL[c.status]}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Row k="Juste titre (acte translatif)" v={d.juste_titre_label} />
            <Row k="Bonne foi au moment de l'acte" v={d.bonne_foi_label} />
          </div>
        </Section>

        <Section title="Documents">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <div className="fc-mono mb-2 text-[11px] uppercase tracking-wider" style={{ color: "var(--fc-teal)" }}>Disponibles</div>
              <ul className="space-y-1.5 text-sm text-foreground">
                {d.documents_present.length ? d.documents_present.map((x) => <li key={x}>• {x}</li>) : <li>—</li>}
              </ul>
            </div>
            <div>
              <div className="fc-mono mb-2 text-[11px] uppercase tracking-wider" style={{ color: "var(--fc-secondary-text)" }}>À réunir</div>
              <ul className="space-y-1.5 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
                {d.documents_missing.length ? d.documents_missing.map((x) => <li key={x}>• {x}</li>) : <li>—</li>}
              </ul>
            </div>
          </div>
        </Section>

        {d.notoriete.applicable && (
          <div className="fc-print-page rounded-2xl border p-6 sm:p-8" style={{ backgroundColor: "var(--fc-surface2)", borderColor: "hsl(var(--border))" }}>
            <h2 className="mb-2 font-serif text-lg font-semibold text-foreground">Acte de notoriété acquisitive</h2>
            <p className="text-sm leading-relaxed text-foreground">{d.notoriete.text}</p>
          </div>
        )}

        <Section title="Recommandation">
          <p className="text-sm leading-relaxed text-foreground">{d.recommandation}</p>
        </Section>

        <p className="fc-mono px-1 text-[11px] leading-relaxed" style={{ color: "var(--fc-secondary-text)" }}>
          Fiche transmise le {d.created_at_fr}. Conformément à l'article L221-28 13° du Code de la consommation,
          vous avez expressément renoncé à votre droit de rétractation de 14 jours afin de permettre la
          livraison immédiate de ce contenu numérique.
          {d.is_demo && " Ce document a été généré via un code promo interne AUPRÉA : aucun paiement réel n'a été perçu."}
        </p>
      </main>
      <Footer />
    </div>
  );
}
